/* The enquiry form's client-side rules — a MIRROR of
 * EnterpriseEnquiries::Questions.visible? and the required check in
 * EnterpriseEnquiries::Submission. The server is the truth; this only stops a
 * step advancing with an obvious gap. Change the two together. */

export type Kind =
  | 'text' | 'textarea' | 'email' | 'url' | 'country' | 'single' | 'multi'
  | 'currency' | 'number' | 'statements' | 'files' | 'revenue_band'

export interface Option { key: string; label: string; status?: string | null; status_label?: string | null }

export interface Question {
  key: string
  label: string
  kind: Kind
  required: boolean
  help?: string | null
  options?: Option[]
  show_if?: Record<string, string[]> | null
  hide_if?: Record<string, string[]> | null
}

export interface Step { key: string; title: string; questions: Question[] }

export interface EnquiryPayload {
  preset?: { business_type?: string; entry?: string }
  intro: string
  closing: string
  consent_label: string
  thank_you: string
  contact_email: string
  revenue_bands: string[]
  countries: { code: string; name: string }[]
  steps: Step[]
}

export type Answers = Record<string, unknown>
export interface Draft { step: number; answers: Answers }

export const DRAFT_KEY = 'wavbee.technology.enquiry.v1'

export function isVisible(q: Question, answers: Answers): boolean {
  const value = (key: string) => String(answers[key] ?? '')
  const shown = Object.entries(q.show_if ?? {}).every(([dep, values]) => values.includes(value(dep)))
  const hidden = Object.entries(q.hide_if ?? {}).some(([dep, values]) => values.includes(value(dep)))
  return shown && !hidden
}

export function isBlank(q: Question, value: unknown): boolean {
  if (value == null) return true
  if (q.kind === 'statements') return !((value as { amounts?: string[] }).amounts ?? []).some(a => a.trim())
  if (Array.isArray(value)) return value.length === 0
  return String(value).trim() === ''
}

export const REQUIRED = 'This question needs an answer.'

export function stepErrors(step: Step, answers: Answers): Record<string, string> {
  const errors: Record<string, string> = {}
  for (const q of step.questions) {
    if (q.kind === 'files' || !isVisible(q, answers)) continue
    if (q.required && isBlank(q, answers[q.key])) errors[q.key] = REQUIRED
  }
  return errors
}

export function firstStepWithError(steps: Step[], errors: Record<string, string>): number {
  return steps.findIndex(s => s.questions.some(q => q.key in errors))
}

export function loadDraft(): Draft | null {
  try {
    const raw = localStorage.getItem(DRAFT_KEY)
    if (!raw) return null
    const d = JSON.parse(raw) as unknown
    if (!d || typeof d !== 'object') return null
    const { step, answers } = d as { step?: unknown; answers?: unknown }
    if (typeof step !== 'number' || !Number.isFinite(step) || step < 0) return null
    if (!answers || typeof answers !== 'object' || Array.isArray(answers)) return null
    return { step: Math.floor(step), answers: answers as Answers }
  } catch {
    return null
  }
}

export function saveDraft(draft: Draft): void {
  try {
    localStorage.setItem(DRAFT_KEY, JSON.stringify(draft))
  } catch {
    /* private window or blocked storage — the form still works, it just forgets */
  }
}

export function clearDraft(): void {
  try {
    localStorage.removeItem(DRAFT_KEY)
  } catch {
    /* as above */
  }
}
